import './WorkflowHistory.css'

/**
 * WorkflowHistory Component
 * Displays workflow history timeline with pending approvals
 *
 * @param {Object} props
 * @param {Array} props.history - Array of workflow history actions
 * @param {Array} props.pendingTasks - Array of pending approval tasks
 * @param {Object} props.currentUser - Current user object
 */
const WorkflowHistory = ({ history = [], pendingTasks = [], currentUser = null }) => {
  if (history.length === 0 && pendingTasks.length === 0) {
    return <span className="approval-summary-td-content">—</span>
  }

  // Helper: Check if a task is assigned to current user
  const isCurrentUserTask = (task) => {
    if (!currentUser) return false

    // Match by oxygenId (most reliable)
    const taskOxygenId = task.oxygenId || task.assignee?.oxygenId
    const currentUserOxygenId = currentUser?.userId || currentUser?.sub || currentUser?.oxygenId || currentUser?.id

    if (taskOxygenId && currentUserOxygenId && taskOxygenId === currentUserOxygenId) {
      return true
    }

    // Fallback to name/email matching
    const assigneeName = task.name || task.assignee?.name || ''
    const assigneeEmail = task.assignee?.email || ''
    const userName = currentUser?.name || currentUser?.displayName || ''
    const userEmail = currentUser?.email || ''

    return assigneeName.toLowerCase() === userName.toLowerCase() ||
           assigneeEmail.toLowerCase() === userEmail.toLowerCase()
  }

  // Check if any pending task is assigned to current user
  const isWaitingForCurrentUser = pendingTasks.some(task => isCurrentUserTask(task))

  // Helper: Check if a history item is waiting for current user
  const isHistoryItemWaitingForCurrentUser = (action) => {
    // Check if this action has a corresponding pending task for current user
    // This handles cases like "requestInput" that appear in history but are still pending
    if (!currentUser) return false

    const actionUser = action.operator?.name || action.operator?.email || ''
    const currentUserName = currentUser?.name || currentUser?.displayName || ''
    const currentUserEmail = currentUser?.email || ''

    // Check if action is directed at current user
    const isTargetedAtCurrentUser =
      actionUser.toLowerCase() === currentUserName.toLowerCase() ||
      actionUser.toLowerCase() === currentUserEmail.toLowerCase()

    // Also check if there's a pending task that matches this action
    const hasPendingTaskForCurrentUser = pendingTasks.some(task =>
      isCurrentUserTask(task) &&
      (action.id === task.id || action.stepId === task.stepId)
    )

    return isTargetedAtCurrentUser || hasPendingTaskForCurrentUser
  }

  // Helper: Get action icon
  const getActionIcon = (actionType) => {
    if (actionType?.includes('proceed') || actionType?.toLowerCase().includes('approve')) return '✓'
    if (actionType?.includes('reject')) return '✕'
    if (actionType?.includes('setToInReview') || actionType?.includes('status')) return '➜'
    return '•'
  }

  // Helper: Get action color
  const getActionColor = (actionType) => {
    if (actionType?.includes('proceed') || actionType?.toLowerCase().includes('approve')) return '#10b981'
    if (actionType?.includes('reject')) return '#ef4444'
    if (actionType?.includes('setToInReview') || actionType?.includes('status')) return '#3b82f6'
    return '#64748b'
  }

  // Helper: Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Helper: Get action label
  const getActionLabel = (action) => {
    const actionType = action.action || action.actionType || 'Action'

    // Map action types to friendly labels
    if (actionType === 'setToInReview') return 'Submitted for Review'
    if (actionType === 'review:proceed') {
      return action.stepName ? `Approved by ${action.stepName}` : 'Approved'
    }
    if (actionType === 'review:reject') {
      return action.stepName ? `Rejected by ${action.stepName}` : 'Rejected'
    }
    if (actionType.includes('approve')) return 'Approved'
    if (actionType.includes('reject')) return 'Rejected'
    if (actionType.includes('status')) return 'Status Changed'

    return actionType
  }

  return (
    <div className="workflow-history-container">
      {/* Pending Approvals Banner */}
      {pendingTasks.length > 0 && (
        <div
          className={`workflow-pending-box ${isWaitingForCurrentUser ? 'current-user' : ''}`}
          style={{
            background: isWaitingForCurrentUser
              ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
              : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            boxShadow: isWaitingForCurrentUser
              ? '0 1px 4px rgba(59, 130, 246, 0.3)'
              : '0 1px 2px rgba(0, 0, 0, 0.1)',
            animation: isWaitingForCurrentUser ? 'pulse 2s infinite' : 'none'
          }}
        >
          <div className="workflow-pending-header">
            <span className="workflow-pending-label">Pending</span>
          </div>
          {pendingTasks.map((task, taskIdx) => {
            const approverName = task.name || task.assignee?.name || task.assignee?.email || 'Unknown'
            const dueDate = task.dueDate ? formatDate(task.dueDate) : ''
            const isUserTask = isCurrentUserTask(task)

            return (
              <div
                key={task.id || `task-${taskIdx}`}
                className={`workflow-task-item ${isUserTask ? 'current-user' : ''}`}
                style={{
                  marginTop: taskIdx > 0 && isUserTask ? '4px' : '0'
                }}
              >
                <div>
                  Waiting for: <strong>{approverName}</strong>
                  {isUserTask && <span className="workflow-task-you-label">← YOU</span>}
                </div>
                {dueDate && <div className={`workflow-task-due ${isUserTask ? 'current-user' : ''}`}>Due: {dueDate}</div>}
              </div>
            )
          })}
        </div>
      )}

      {/* Workflow History Timeline */}
      {history.map((action, idx) => {
        const actionLabel = getActionLabel(action)
        const actionType = action.action || action.actionType || 'Action'
        const icon = getActionIcon(actionType)
        const color = getActionColor(actionType)
        const userName = action.operator?.name || action.operator?.email || 'System'
        const timestamp = formatDate(action.createdAt || action.performedAt || action.timestamp)
        const comment = action.comment || action.note || action.message || ''
        const isAwaitingCurrentUser = isHistoryItemWaitingForCurrentUser(action)

        return (
          <div
            key={action.id || `action-${idx}`}
            className={`workflow-history-item ${isAwaitingCurrentUser ? 'awaiting-current-user' : ''}`}
            style={{
              borderLeft: `3px solid ${isAwaitingCurrentUser ? '#3b82f6' : color}`,
              background: isAwaitingCurrentUser
                ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
                : '#f8fafc',
              boxShadow: isAwaitingCurrentUser
                ? '0 2px 6px rgba(59, 130, 246, 0.3)'
                : 'none'
            }}
          >
            <div className="workflow-history-header">
              <span className="workflow-history-icon" style={{ color: isAwaitingCurrentUser ? '#3b82f6' : color }}>{icon}</span>
              <span className="workflow-history-action">{actionLabel}</span>
              {isAwaitingCurrentUser && (
                <span className="workflow-awaiting-badge">← Awaiting Your Response</span>
              )}
            </div>
            <div className="workflow-history-user">{userName}</div>
            <div className="workflow-history-timestamp">{timestamp}</div>
            {comment && (
              <div className="workflow-history-comment">"{comment}"</div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default WorkflowHistory
